import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionDisplay,
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isUniquePurchaserName,
  isValidPercentageDecimal,
  isValidPostCode,
  isValidTotalCropSalePercentage,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingCropPurchasers,
  useGetFootprintingCropSales,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingCropSales,
  useTranslation,
} from "utils";

import { TRANSPORT_COUNTRY_CODE } from "consts";

import { CropSaleData, PurchaserData } from "models";

import { Button, Card, Flex, Form, FormInstance, Icon, IconButton, Input, InputNumber, KlimRadioGroup, Option, PageSpin, Select, Tooltip } from "components";

interface CropTransportFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
}

export const CropTransportForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropTransportFormProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [cropSaleActive, setCropSaleActive] = useState<boolean | null>(null);
  const [cropSales, setCropSales] = useState<CropSaleData[]>([]);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const purchasersQuery = useGetFootprintingCropPurchasers(farmId);
  const cropSalesQuery = useGetFootprintingCropSales(primaryDataId ?? "");

  useEffect(() => {
    if (purchasersQuery.data && cropSalesQuery.data && cropSalesQuery.data.length > 0) {
      const existingEntries = cropSalesQuery.data.map((sale) => {
        const purchaserMatch = purchasersQuery.data?.find((purchaser) => purchaser.id === sale.purchaserId) as PurchaserData | undefined;

        return {
          id: sale.id,
          purchaserId: sale.purchaserId ?? "",
          purchaser: {
            id: sale.purchaserId ?? "",
            farmId: farmId,
            name: purchaserMatch?.name ?? null,
            postCode: purchaserMatch?.postCode ?? null,
            countryCode: purchaserMatch?.countryCode ?? null,
          },
          soldYieldPct: sale.soldYieldPct,
        };
      });

      if (formRef.current) {
        existingEntries.forEach((entry, index) => {
          formRef.current?.setFieldsValue({
            [`country-${index}`]: entry.purchaser?.countryCode,
            [`purchaserName-${index}`]: entry.purchaser?.name,
            [`postcode-${index}`]: entry.purchaser?.postCode,
            [`soldYieldPct-${index}`]: formatFractionDisplay(entry.soldYieldPct),
          });
        });
      }

      setCropSales(existingEntries);
    } else if (cropSales.length === 0 && primaryDataId) {
      addCropSale();
    }
  }, [purchasersQuery.data, cropSalesQuery.data, primaryDataId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setCropSaleActive(cropPrimaryDataQuery.data.cropSaleActive ?? null);

      formRef.current?.setFieldsValue({
        cropSaleActive: cropPrimaryDataQuery.data.cropSaleActive ?? null,
      });

      if (cropPrimaryDataQuery.data.cropSaleActive !== null) {
        formRef.current?.validateFields();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  const { mutate: mutateCropSales } = useMutateFootprintingCropSales(
    farmId,
    cropId,
    year,
    {
      footprintingFarmCropPrimaryDataId: primaryDataId ?? "",
      cropSales,
      farmId,
    },
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      cropSaleActive,
    },
    primaryDataId
  );

  const updatePurchaserField = (index: number, field: keyof PurchaserData, value: string | number) => {
    const updatedCropSales = [...cropSales];
    updatedCropSales[index] = {
      ...updatedCropSales[index],
      purchaser: {
        ...updatedCropSales[index].purchaser,
        [field]: value,
      },
    };
    setCropSales(updatedCropSales);
  };

  const updateCropSaleField = (index: number, value: number | null) => {
    const updatedCropSales = [...cropSales];
    updatedCropSales[index].soldYieldPct = value ?? null;
    setCropSales(updatedCropSales);
  };

  const addCropSale = () => {
    if (!primaryDataId) return;

    setCropSales([
      ...cropSales,
      {
        purchaser: {
          farmId,
          name: null,
          postCode: null,
          countryCode: null,
        },
        soldYieldPct: null,
      },
    ]);
  };

  const deleteCropSale = (index: number) => {
    if (cropSales.length <= 1) return;

    const updatedCropSales = [...cropSales];
    updatedCropSales.splice(index, 1);
    setCropSales(updatedCropSales);
  };

  const isLoading = [cropPrimaryDataQuery, cropSalesQuery, purchasersQuery].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropSales();
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateCropSales, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropTransportForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropTransportForm.info")}</p>
        </Flex>
      </Card>

      <Card dataTestId="footprinting-crop-transport-form" className="margin-bottom-100 padding-100">
        <Form ref={formRef}>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <span style={{ maxWidth: "60%" }}>
              {t("cropTransportForm.question.radioQuestion")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropTransportForm.tooltips.radioQuestion")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              required
              name="cropSaleActive"
              dataTestId="footprinting-crop-transport-form-radio"
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setCropSaleActive(target.value);
              }}
              value={cropSaleActive}
              defaultValue={cropSaleActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {cropSaleActive &&
            cropSales.map((sale, index) => (
              <div key={sale.id || index}>
                <Flex className="padding-100 padding-bottom-0" justify="space-between" align="center" vertical={false}>
                  <h5 style={{ flex: 1 }}>{t("cropTransportForm.purchaser") + (index + 1)}</h5>
                  {cropSales.length > 1 && <IconButton type="tertiary" size="small" iconName="tabler/trash" onClick={() => deleteCropSale(index)} />}
                </Flex>
                <div className="padding-100 padding-bottom-0">
                  <Input
                    className="floating-label"
                    dataTestId={`footprinting-crop-transport-form-name-input-${index}`}
                    disabled={readOnly}
                    name={`purchaserName-${index}`}
                    label={t("cropTransportForm.question.purchaserNameQuestionLabel")}
                    tooltip={t("cropTransportForm.tooltips.purchaserNameQuestionLabel")}
                    value={sale.purchaser?.name ?? ""}
                    initialValue={sale.purchaser?.name ?? ""}
                    onChange={(e) => updatePurchaserField(index, "name", e.target.value)}
                    message={t("cropTransportForm.error.purchaserNameQuestionError")}
                    rules={[
                      { required: true },
                      {
                        validator: (_: unknown, value: string) =>
                          isUniquePurchaserName(cropSales, value, index) ? Promise.resolve() : Promise.reject(new Error("Purchaser name must be unique")),
                      },
                    ]}
                  />
                </div>
                <div className="padding-100 padding-bottom-0">
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    className="floating-label"
                    disabled={readOnly}
                    dataTestId={`footprinting-crop-transport-form-country-select-${index}`}
                    name={`country-${index}`}
                    label={t("cropTransportForm.question.countryQuestionLabel")}
                    tooltip={t("cropTransportForm.tooltips.countryQuestionLabel")}
                    value={sale.purchaser?.countryCode}
                    initialValue={sale.purchaser?.countryCode}
                    onChange={(value) => updatePurchaserField(index, "countryCode", value || null)}
                    allowClear
                    rules={[{ required: true, message: t("cropTransportForm.error.countryQuestionError") }]}
                    dropdownStyle={{ zIndex: 10010 }}
                  >
                    {Object.entries(TRANSPORT_COUNTRY_CODE).map(([name, code]) => (
                      <Option disabled={readOnly} key={`country-select-${index}-${code}`} label={t(name)} value={code}>
                        {t(`countries.${code}`)}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="padding-100 padding-bottom-0">
                  <Input
                    className="floating-label"
                    dataTestId={`footprinting-crop-transport-form-postcode-input-${index}`}
                    name={`postcode-${index}`}
                    disabled={readOnly}
                    label={t("cropTransportForm.question.postcodeQuestionLabel")}
                    tooltip={t("cropTransportForm.tooltips.postcodeQuestionLabel")}
                    value={sale.purchaser?.postCode ?? ""}
                    initialValue={sale.purchaser?.postCode ?? ""}
                    onChange={(e) => updatePurchaserField(index, "postCode", e.target.value)}
                    message={t("cropTransportForm.error.postcodeQuestionError")}
                    rules={[
                      { required: true },
                      {
                        validator: (_: unknown, value: string) => {
                          if (isValidPostCode(value, sale.purchaser.countryCode ?? "")) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("cropTransportForm.error.postcodeQuestionError")));
                        },
                      },
                    ]}
                  />
                </div>
                <div className="padding-100">
                  <InputNumber
                    className="floating-label"
                    name={`soldYieldPct-${index}`}
                    disabled={readOnly}
                    label={t("cropTransportForm.question.shareQuestionLabel")}
                    tooltip={t("cropTransportForm.tooltips.shareQuestionLabel")}
                    dataTestId={`footprinting-crop-transport-form-yield-input-${index}`}
                    suffix="%"
                    value={formatFractionInput(sale.soldYieldPct)}
                    initialValue={formatFractionInput(sale.soldYieldPct)}
                    onChange={(v) => updateCropSaleField(index, formatPercentageInput(v))}
                    precision={2}
                    validateFirst
                    rules={[
                      { required: true, message: t("cropTransportForm.error.saleQuestionError") },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                            return Promise.reject(new Error(t("cropTransportForm.error.saleQuestionError")));
                          }

                          if (!isValidTotalCropSalePercentage(cropSales, v, index)) {
                            return Promise.reject(new Error(t("cropTransportForm.error.totalEquals100Percent")));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </div>
                {index === cropSales.length - 1 && (
                  <Button type="text" disabled={readOnly} onClick={addCropSale}>
                    <Icon iconName="klim/plus" className="icon inline" />
                    {t("cropTransportForm.add")}
                  </Button>
                )}
              </div>
            ))}
          <div />
        </Form>
      </Card>
    </div>
  );
};
